import { observable, action, runInAction } from 'mobx'
import { StoreExt } from '@utils/reactExt'
import { orderListStore } from '@views/mine/ordering/actor/store'
import { upgradesStore } from '@views/mine/upgrades/actor/store'
import moment from 'moment'
import { loginStore } from '@views/login/store'
import EventTracking from '@src/model/eventTracking'
import { GOODS_ACTIVITY_TYPE_V, OPERATOR_V } from '@src/constants'
import { getUrlSearch } from '@src/utils';

interface UserInfo {
    laoCustomerBindDtos?: any[]
    laoCustomerDto?: any
    lenovoId?: any
    currentIccid?: string
    transactionId?: string
    bind?: boolean
    exist?: boolean
    iccid?: string
}

interface CurrentDeviceInfo {
    simId?: any
    modelName?: any
    nickName?: any
    goodsReleaseName?: any
    planName?: string // 套餐名称
    productOrderingTime?: string // 订购时间
    quantityMax?: string // 流量
    quantityUnit?: string // 流量单位
    startDate?: string // 生效日期
    endDate?: string // 失效日期
    monthUsage?: string // 月用量
    dayUsage?: string // 日用量
    speedState?: string
    stop?: any // 卡锁定
    outDate?: any // 套餐到期
    userGoodsId?: string
    operatorsId?: number
}

class UserStore extends StoreExt {
    @observable loading: boolean = false
    @observable authCodes: any = []
    @observable requestSimInfoFinish: boolean = false
    @observable requestInitFinish: boolean = false
    @observable getFreeSuccess: boolean = false
    @observable showActiveTiplMask: boolean = false // 激活提示mask
    @observable deviceList: any[] = []
    @observable currentDeviceInfo: CurrentDeviceInfo = {}
    @observable initInfo: any = {}
    @observable activeResult: any = {}
    @observable initPackage: any = {}
    @observable currentPath: string = ''
    @observable wechatCode: string = ''
    @observable wxConfig: any = {}
    @observable payConfig: any = ''
    @observable displayCouponNoticeInfo: boolean = false
    @observable couponNoticeInfo: any = {}
    @observable activePackageState: 'init' | 'choose' | 'done' | 'close' = 'init'
    @observable userInfo: UserInfo = {
        laoCustomerBindDtos: [],
        laoCustomerDto: {},
        currentIccid: '',
        bind: false
    }
    @observable waitBindingCard: any = {
        simId: '',
        nickName: '待绑定'
    }
    @observable currentKey: string
    @observable initPackageArr: any[] = []
    private initActiveTip: Set<string> = new Set()

    @action
    setActiveState = (state) => {
        this.activePackageState = state
    }

    @action
    setInitPackage = (data) => {
        data.planName = data.goodsReleaseName
        this.initPackage = data
    }

    @action
    getInitPackageArr = async (iccid) => {
        try {
            const res = await this.api.querySelectActive({ iccid: iccid })
            res.map((item) => {
                item.policyDegree = item.packageTraffic
                if (!!item.productInfo) {
                    item.infoLists = item.productInfo.split('\n')
                } else {
                    item.infoLists = []
                }
            })
            runInAction(() => {
                this.initPackageArr = res
            })
        } catch (e) {
        }
    }

    @action
    initMenuKey = () => {
        if (location.href.indexOf('app/home') > -1) {
            this.setKey('home')
        } else if (location.href.indexOf('app/mall') > -1) {
            this.setKey('mall')
        } else if (location.href.indexOf('app/company') > -1) {
            this.setKey('company')
        } else if (location.href.indexOf('app/service') > -1) {
            this.setKey('service')
        } else if (location.href.indexOf('app/userCenter') > -1) {
            this.setKey('userCenter')
        }
    }

    @action
    setKey = (value) => {
        this.currentKey = value
    }
    @action
    setFreeSuccess = (value, result?) => {
        this.getFreeSuccess = value
        runInAction(() => {
            this.activeResult = result
        })
    }

    /**
     * 获取用户信息
     */
    @action
    getUserInfo = async (cache: boolean = true, sign?, fn?): Promise<any> => {
        if (!cache || !this.userInfo) {
            runInAction(() => this.loading = true)
            const res = await this.api.getUserInfo({ sign: sign }).catch(e => e)
            if (!res) {
                return
            }
            localStorage.toKey = res.toKey
            runInAction(() => {
                this.userInfo = res
                this.deviceList = res.laoCustomerBindDtos || []
            })
            if (!!fn) {
                fn()
            }
            this.getInitInfo();
        }
        runInAction(() => this.loading = false)
    }

    /**
     * 获取当前设备
     */
    @action
    getInitInfo = async () => { // 首页获取url内设备的信息
        try {
            const res = await this.api.getUrlDeviceInfo({})
            res.infoLists = !!res.productInfo ? res.productInfo.split('\n') : []
            runInAction(() => {
                this.initInfo = res
            })
            if (getUrlSearch().chl === 'tabletApp' && !!res.imsi && !res.simId) {
                this.goToPath('/global/empty-card')
            }
            this.getDeviceDetail(res.simId);  // simInfo接口
        } catch (e) {
        }
    }

    @action
    setDisplayCouponNoticeInfo = (value) => {
        this.displayCouponNoticeInfo = value
    }

    @action
    getCouponReceiveNotice = async () => {
        try {
            const res = await this.api.getCouponReceiveNotice({})
            runInAction(() => {
                this.couponNoticeInfo = res[0]
                this.displayCouponNoticeInfo = true
            })
        } catch (e) {
        }
    }

    // 赠送套餐
    @action
    getInitPackage = async (id, iccid) => {
        try {
            const res = await this.api.getPackageDetail({ goodsReleaseId: id, simId: iccid })
            const hasExisted = !this.initActiveTip.has(iccid)
            if (!!res.productInfo) {
                res.infoLists = res.productInfo.split('\n')
            } else {
                res.infoLists = []
            }
            const { activityPlanConfDtos } = res
            if (Array.isArray(activityPlanConfDtos) && activityPlanConfDtos.length > 0) {
                let activity = {} as any
                activityPlanConfDtos.map(
                    item => activity = item.actCode === GOODS_ACTIVITY_TYPE_V.ACTIVE_FREE ? item : {}
                )
                res.actInfoList = activity.productActInfo.split('\n')
            }

            runInAction(() => {
                this.initPackage = res
                this.showActiveTiplMask = hasExisted // 首次显示
            })
        } catch (e) {
        }
    }

    @action
    setPath = (path) => runInAction(() => this.currentPath = path)

    @action
    logout = async (param = {}) => {
        try {
            await this.api.loginOut(param)
            runInAction(() => {
                this.userInfo = {
                    laoCustomerBindDtos: [],
                    laoCustomerDto: {}
                }
                this.currentDeviceInfo = {}
            })

            this.goToPath('/login')
            loginStore.refresh() // 刷新验证码
        } catch (err) {
        }
    }

    @action
    setWaitBindingCard = async (simId) => {
        runInAction(() => this.waitBindingCard.simId = simId)
    }

    // 查询 页面 配置  如果没绑卡 请先绑定 号卡
    @action
    pageConfigQuery = async () => {
        try {
            const res = await this.api.pageConfigQuery({ category: 'alipay', name: 'returnUrl' })
            runInAction(() => {
                this.wxConfig = res
                this.payConfig = res
            })
            // if (this.wxConfig === '1' || backDoorList.indexOf(this.initInfo.simId) > -1) {
            //     this.getWechatCode(this.initInfo.simId)
            // }
        } catch (err) {
        }
    }

    // 流量图表
    @action
    getCardFlowDetail = async () => {
        try {
            const res = await this.api.getCardFlowDetail({})
            runInAction(() => {
                this.currentDeviceInfo = { ...this.currentDeviceInfo, ...res }
            })
        } catch (e) {
        }
    }

    /**
     * 设备信息
     */
    @action
    getDeviceDetail = async (simId?) => {
        try {
            // sim/info 接口
            const res = await this.api.getCardUserInfo({ simId: simId })
            if (res.active === false) {
                this.getInitPackageArr(simId)
                await this.getInitPackage(res.initproduct, simId) // 查询套餐信息
            }

            res.allowUpdate = moment().isBefore(moment(res.endDate).subtract(1, 'months'))
            // 设备信息
            const _currentDeviceInfo = res
            const obj = this.deviceList.find(ele => ele.simId === res.simId) as any || {}
            if (obj.nickName) {
                _currentDeviceInfo.nickName = obj.nickName
            }
            runInAction(() => {
                this.currentDeviceInfo = { ...this.currentDeviceInfo, ..._currentDeviceInfo }
            })
            if (res.operatorsId === OPERATOR_V.TRUPHONE || res.operatorsId === OPERATOR_V.HUE) {
                const hashPath = location.hash;
                if (hashPath.indexOf('#/login') > -1) {
                    this.goToPath(hashPath.replace('#/login', '/global/login'))
                }
                if (hashPath.indexOf('#/app') > -1) {
                    this.goToPath(hashPath.replace('#/app', '/global'))
                }
            } else {
                location.href = `#/jump-to-weApp?iccid=${this.currentDeviceInfo.simId}`
            }

            if (location.href.indexOf('app/mine/ordering/list') > -1) {
                location.href = `#/app/mine/ordering/list`
                orderListStore.getOrderList(simId)
            }
            if (location.href.indexOf('app/mine/upgrade/list') > -1) {
                location.href = `#/app/mine/upgrade/list`
                upgradesStore.getList(simId)
            }

        } catch (err) {
            console.log(err)
        }
    }

    @action
    setUserInfo = (userInfo: any) => runInAction(() => this.userInfo = userInfo)

    @action
    updateSimId = (id) => runInAction(() => {
        this.initInfo.simId = id
        this.userInfo.currentIccid = id
    })
    @action
    getAuthCodeByICCID = async () => {
        if (location.href.indexOf('global') > -1) {
            return
        }
        let authCodes = []
        try {
            const res = await this.api.getAuthCodeByICCID({ appCode: '3' })
            authCodes = res.authCode
        } catch (e) {
            authCodes = []
        } finally {
            localStorage.setItem('authCodes', JSON.stringify(authCodes))
        }
    }

    /**
     * 埋点
     */
    @action
    sendEventTracing = async (param: EventTracking) => {
        await this.api.sendEventTracing(param)
    }

    @action
    setShowActiveTipMask = (v: boolean, iccid?: string) => {
        // 用于刷新、卡片切换 是否显示mask
        this.initActiveTip.add(iccid)
        this.showActiveTiplMask = v
    }
}

const userStore = new UserStore()
export { userStore, UserStore, UserInfo }
